import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface EditionFeedbackSubscriptionPreviewResult {
  subscriptionId?: number;
  subscriptionTitleName?: string;
  editionId?: number;
  theme?: string;
  productId?: number;
  expectedCatalogProductName?: string;
  expectedFormTitle?: string;
  referenceEditionId?: number;
  referenceFormId?: number;
  formId?: number;
  status?: string;
  errorCode?: string;
  errorMessage?: string;
  actionHint?: string;
}

export interface EditionFeedbackAutomationPreviewResponse {
  executionId?: string;
  referenceMonth?: string;
  validated?: boolean;
  hasErrors?: boolean;
  readyToCreateCount?: number;
  createdCount?: number;
  skippedCount?: number;
  failedCount?: number;
  errorSummaries?: string[];
  results?: EditionFeedbackSubscriptionPreviewResult[];
}

export interface EditionFeedbackHistoryItem {
  id?: number;
  executionId?: string;
  triggeredBy?: string;
  referenceMonth?: string;
  subscriptionId?: number;
  subscriptionTitleName?: string;
  editionId?: number;
  theme?: string;
  productId?: number;
  formId?: number;
  formTitle?: string;
  referenceFormId?: number;
  status?: string;
  step?: string;
  errorMessage?: string;
  createdAt?: string;
}

export interface EditionFeedbackSchedulerStatus {
  enabled?: boolean;
  cronExpression?: string;
  timezone?: string;
  scheduleDescription?: string;
  jobName?: string;
  updatedBy?: string;
  updatedAt?: string;
  provisionAvailable?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EditionFeedbackAutomationService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/feedback-admin/edition-feedback`;

  preview(triggeredBy = 'manual'): Observable<EditionFeedbackAutomationPreviewResponse> {
    const params = new HttpParams().set('triggeredBy', triggeredBy);

    return this.http
      .post<ResponseB4A<EditionFeedbackAutomationPreviewResponse>>(
        `${this.baseUrl}/preview`,
        {},
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result ?? {}));
  }

  provision(triggeredBy = 'manual'): Observable<EditionFeedbackAutomationPreviewResponse> {
    const params = new HttpParams().set('triggeredBy', triggeredBy);

    return this.http
      .post<ResponseB4A<EditionFeedbackAutomationPreviewResponse>>(
        `${this.baseUrl}/provision`,
        {},
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(map((response) => response.result ?? {}));
  }

  getHistory(filters?: {
    referenceMonth?: string;
    status?: string;
    triggeredBy?: string;
  }): Observable<EditionFeedbackHistoryItem[]> {
    let params = new HttpParams();
    if (filters?.referenceMonth && filters.referenceMonth !== 'all') {
      params = params.set('referenceMonth', filters.referenceMonth);
    }
    if (filters?.status && filters.status !== 'all') {
      params = params.set('status', filters.status);
    }
    if (filters?.triggeredBy && filters.triggeredBy !== 'all') {
      params = params.set('triggeredBy', filters.triggeredBy);
    }

    return this.http
      .get<ResponseB4A<EditionFeedbackHistoryItem[]>>(`${this.baseUrl}/logs`, {
        headers: this.authHeaders(),
        params
      })
      .pipe(map((response) => response.result ?? []));
  }

  getSchedulerStatus(): Observable<EditionFeedbackSchedulerStatus> {
    return this.http
      .get<ResponseB4A<EditionFeedbackSchedulerStatus>>(`${this.baseUrl}/scheduler`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result ?? {}));
  }

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }
}
